// lib
import React from 'react';
import styled from 'styled-components';

// twicapp-ui
import Caption from '@joinforma/ui/components/Caption';
import Title2 from '@joinforma/ui/components/Title2';

// src
import { renderCountryName } from 'utils/employee';
import { Address } from './types';
import { PRIVACY } from 'constants/smartlook';

const ContentStyled = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 64px;
`;

const RowStyled = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 15px;
  &:not(:last-child) {
    padding-bottom: 15px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.background.tertiary};
  }
`;

const InfoStyled = styled.div`
  display: flex;
  padding-bottom: 15px;
  justify-content: flex-start;
`;

const SecondaryTitleStyled = styled(Title2)`
  color: ${({ theme }) => theme?.colors?.text?.secondary};
  display: flex;
  flex-direction: column;
  flex: 0 0 160px;
`;

const OptionalTextStyled = styled(Caption)`
  color: ${({ theme }) => theme?.colors?.text?.secondary};
`;

const TextStyled = styled(Title2)``;

const CaptionStyled = styled(Caption)`
  color: ${({ theme }) => theme?.colors?.text?.secondary};
`;

type AdditionalInfoSectionProps = {
  mailing_address_description_placeholder: string;
  show_phone: boolean;
  preferred_first_name: string;
  preferred_last_name: string;
  personal_email: string;
  address?: Address;
  phone?: string;
};

export const AdditionalInfoSection = ({
  show_phone,
  mailing_address_description_placeholder,
  preferred_first_name,
  preferred_last_name,
  personal_email,
  address: {
    street = '',
    street_ext = '',
    locality = '',
    state = '',
    zip = '',
    country = '',
  } = {},
  phone,
}: AdditionalInfoSectionProps) => {
  const preferred_name =
    `${preferred_first_name} ${preferred_last_name}`.trim();
  const none_entered_text = 'None entered';
  const is_address_empty = !street && !locality && !state && !zip && !country;

  return (
    <ContentStyled>
      <RowStyled>
        <InfoStyled>
          <SecondaryTitleStyled>
            Preferred name
            <OptionalTextStyled>(optional)</OptionalTextStyled>
          </SecondaryTitleStyled>
          <TextStyled>
            {preferred_name ? `${preferred_name}` : `${none_entered_text}`}
          </TextStyled>
        </InfoStyled>
        <CaptionStyled>
          This is how your name will appear across your Forma account.
        </CaptionStyled>
      </RowStyled>
      <RowStyled>
        <InfoStyled>
          <SecondaryTitleStyled>
            Personal email
            <OptionalTextStyled>(optional)</OptionalTextStyled>
          </SecondaryTitleStyled>
          <TextStyled data-sl={PRIVACY.MASK}>
            {personal_email ? `${personal_email}` : `${none_entered_text}`}
          </TextStyled>
        </InfoStyled>
        <CaptionStyled>
          We send important account information to your work email. If we can't
          contact you through your work email, we'll use your personal email as
          a backup.
        </CaptionStyled>
      </RowStyled>
      {mailing_address_description_placeholder && (
        <RowStyled>
          <InfoStyled>
            <SecondaryTitleStyled>
              Mailing address
              <OptionalTextStyled>(optional)</OptionalTextStyled>
            </SecondaryTitleStyled>
            {is_address_empty ? (
              <TextStyled>{none_entered_text}</TextStyled>
            ) : (
              <TextStyled data-sl={PRIVACY.MASK}>
                {street}
                {street && <br />}
                {street_ext}
                {street_ext && <br />}
                {[[state, zip].join(' '), renderCountryName(country)].reduce(
                  (acc, current) => {
                    if (current && current.trim()) {
                      return `${acc}, ${current}`;
                    }
                    return acc;
                  },
                  locality
                )}
              </TextStyled>
            )}
          </InfoStyled>
          <CaptionStyled>
            {`Your ${mailing_address_description_placeholder} will be shipped to this mailing address by default. 
      It can be different than your primary residence.`}
          </CaptionStyled>
        </RowStyled>
      )}
      {show_phone && (
        <RowStyled>
          <InfoStyled>
            <SecondaryTitleStyled>
              Phone number <OptionalTextStyled>(optional)</OptionalTextStyled>
            </SecondaryTitleStyled>
            <TextStyled data-sl={PRIVACY.MASK}>
              {phone ? `${phone}` : `${none_entered_text}`}
            </TextStyled>
          </InfoStyled>
          <CaptionStyled>
            We'll only send you text messages with updates about Forma card
            transactions or accessing Forma if we can't contact you by email.
          </CaptionStyled>
        </RowStyled>
      )}
    </ContentStyled>
  );
};
